import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/* COCO_PRODUCT_GALLERY_600_JS */
(() => {
  const apply = () => {
    document.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
      gallery.dataset.cocoGallery600 = '1';
      gallery.style.setProperty('display','grid','important');
      gallery.style.setProperty('grid-template-columns','72px 600px','important');
      gallery.style.setProperty('gap','16px','important');
      gallery.style.setProperty('max-width','688px','important');
      gallery.style.setProperty('align-items','start','important');
      gallery.style.setProperty('justify-content','start','important');

      const slideshow = gallery.querySelector('slideshow-component');
      if (slideshow) {
        slideshow.style.setProperty('width','600px','important');
        slideshow.style.setProperty('max-width','600px','important');
        slideshow.style.setProperty('grid-column','2','important');
      }

      gallery.querySelectorAll('slideshow-container, slideshow-slides, slideshow-slide, .product-media-container, .product-media').forEach((el) => {
        el.style.setProperty('width','600px','important');
        el.style.setProperty('max-width','600px','important');
      });
      gallery.querySelectorAll('slideshow-slide, .product-media-container, .product-media, .product-media img, slideshow-slide img').forEach((el) => {
        el.style.setProperty('height','600px','important');
        el.style.setProperty('max-height','600px','important');
      });
      gallery.querySelectorAll('img').forEach((img) => {
        img.style.setProperty('object-fit','contain','important');
        img.style.setProperty('background','#fff','important');
      });

      let thumbs = gallery.querySelector('.coco-gallery-thumbs');
      if (!thumbs) {
        thumbs = document.createElement('div');
        thumbs.className = 'coco-gallery-thumbs';
        gallery.insertBefore(thumbs, gallery.firstElementChild);
      }
      thumbs.style.cssText = 'grid-column:1!important;width:72px!important;display:flex!important;flex-direction:column!important;gap:8px!important;align-items:center!important;';
      thumbs.innerHTML = '';
      const slides = [...gallery.querySelectorAll('slideshow-slide')];
      const images = slides.map((slide) => slide.querySelector('img')).filter(Boolean);
      images.forEach((image, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'coco-gallery-thumb';
        button.setAttribute('aria-label', 'Show image ' + (index + 1));
        button.style.cssText = 'width:64px!important;height:64px!important;padding:0!important;border:1px solid #ddd!important;background:#fff!important;border-radius:6px!important;overflow:hidden!important;cursor:pointer!important;';
        const clone = image.cloneNode();
        clone.removeAttribute('srcset');
        clone.style.cssText = 'width:64px!important;height:64px!important;object-fit:cover!important;display:block!important;';
        button.appendChild(clone);
        button.addEventListener('click', () => {
          const slide = slides[index];
          if (slide) slide.scrollIntoView({behavior:'smooth',inline:'center',block:'nearest'});
          gallery.querySelectorAll('.coco-gallery-thumb').forEach((b) => b.style.borderColor = '#ddd');
          button.style.borderColor = '#111';
        });
        if (index === 0) button.style.borderColor = '#111';
        thumbs.appendChild(button);
      });

      gallery.querySelectorAll('slideshow-arrows, .slideshow-controls').forEach((el) => {
        el.style.setProperty('display','flex','important');
        el.style.setProperty('opacity','1','important');
        el.style.setProperty('visibility','visible','important');
        el.style.setProperty('pointer-events','auto','important');
      });
      gallery.querySelectorAll('.mobile\\:hidden').forEach((el) => el.style.setProperty('display','flex','important'));
      gallery.querySelectorAll('.slideshow-control').forEach((button) => {
        button.style.setProperty('display','flex','important');
        button.style.setProperty('width','44px','important');
        button.style.setProperty('height','44px','important');
        button.style.setProperty('border-radius','999px','important');
        button.style.setProperty('background','rgba(255,255,255,.94)','important');
        button.style.setProperty('box-shadow','0 4px 16px rgba(0,0,0,.16)','important');
        button.style.setProperty('color','#111','important');
        button.style.setProperty('opacity','1','important');
        button.style.setProperty('align-items','center','important');
        button.style.setProperty('justify-content','center','important');
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply); else apply();
  window.addEventListener('load', apply);
  setTimeout(apply, 500);
  setTimeout(apply, 1500);
})();
